import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import PageTransition from '../components/PageTransition';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { FiCheck, FiRefreshCw, FiLoader, FiClock, FiCalendar, FiInfo, FiX, FiZap, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import TimelineAvailability from '../components/TimelineAvailability';
import DateRangePicker from '../components/DateRangePicker';
import { saveUserAvailability, getUserAvailability, clearUserAvailability, checkDatabaseSchema } from '../services/availabilityService';
import { getAvailabilityPreference, getWeekStartDay } from '../services/userPreferencesService';
import { addDays, computeRangeForMode, rangeDayCount, generateDateRange as buildDateRange } from '../utils/dateHelpers';
import AvailabilitySkeleton from '../components/skeletons/AvailabilitySkeleton';
import { trackSetAvailability, trackUpdateAvailability } from '../services/analytics';

// Stable string key for a {startDate, endDate} range. Used to tag in-flight
// availability loads so a stale load (e.g. the mount-time "This Week" fetch
// that resolves after the default view has auto-switched to "Next Week") can
// detect it's outdated and skip committing, rather than clobbering the freshly
// loaded data for the now-active range.
const rangeKeyOf = (range) =>
  `${+new Date(range?.startDate)}|${+new Date(range?.endDate)}`;

function MyAvailability() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Core availability state
  const [loading, setLoading] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  // (saveSuccess state removed - auto-save is silent on success.)
  const [saveError, setSaveError] = useState('');
  const [hasExistingAvailability, setHasExistingAvailability] = useState(false);
  const [isCalendarConnected, setIsCalendarConnected] = useState(false);

  // User preferences from database
  const [availabilityPreference, setAvailabilityPreference] = useState('available');
  const [preferencesLoading, setPreferencesLoading] = useState(true);

  // Load user preferences from database
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) return;
      
      setPreferencesLoading(true);
      try {
        const [preferenceResult, weekStartResult] = await Promise.all([
          getAvailabilityPreference(user),
          getWeekStartDay(user)
        ]);

        setAvailabilityPreference(preferenceResult || 'available');
        setWeekStartsOn(weekStartResult);
      } catch (error) {
        console.error('Error loading preferences:', error);
        // Fallback to defaults on error
        setAvailabilityPreference('available');
        setWeekStartsOn(1);
      } finally {
        setPreferencesLoading(false);
      }
    };
    
    loadPreferences();
  }, [user]);

  // Availability data and settings
  const [availabilityData, setAvailabilityData] = useState({});
  
  // Date range mode controls which days are shown.
  //   'thisWeek' → tomorrow through end of this calendar week
  //   'nextWeek' → first day of next week through end of next week
  //   'custom'   → user picks via DateRangePicker
  // What counts as "end of week" depends on the user's `week_start_day`
  // preference (Sunday-start or Monday-start). Default selection on mount
  // may switch to 'nextWeek' if 'thisWeek' would show fewer than 2 days.
  const [dateRangeMode, setDateRangeMode] = useState('thisWeek');

  // 0 = Sunday, 1 = Monday. Defaults to Monday until the user preference loads.
  const [weekStartsOn, setWeekStartsOn] = useState(1);
  const hasAppliedDefaultModeRef = useRef(false);

  // Date range picker state. Initialised to "this week" so the first paint
  // matches the default toggle selection. Will be re-synced once the
  // user's week-start preference loads.
  const [customDateRange, setCustomDateRange] = useState(() => computeRangeForMode('thisWeek', 1));

  // Refs for debounced auto-save. We keep the latest data in a ref so the
  // timer callback always saves the freshest state, even if React batches
  // multiple changes inside the debounce window.
  const autoSaveTimerRef = useRef(null);
  const latestAvailabilityRef = useRef({});

  // Holds the key of the currently-selected range so an in-flight load can tell
  // whether its result is still relevant when it resolves. Written every render
  // so it always reflects the latest range.
  const activeRangeKeyRef = useRef('');
  activeRangeKeyRef.current = rangeKeyOf(customDateRange);

  // Load user availability when component mounts and preferences are loaded
  useEffect(() => {
    if (user && user.id && !preferencesLoading) {
      // Check database schema before loading data
      checkDatabaseSchema(user).then(({ error, requiresMigration }) => {
        if (error) {
          console.error('❌ Database schema check failed:', error);
          if (requiresMigration) {
            setSaveError('Database migration required. Please contact support.');
          } else {
            setSaveError(`Database error: ${error}`);
          }
        } else {
          loadUserAvailability();
        }
      }).catch(err => {
        console.error('❌ Exception during schema check:', err);
        setSaveError(`Database connection error: ${err.message}`);
      });
    }
  }, [user, preferencesLoading]);

  // Handle preference changes after initial load
  useEffect(() => {
    // Only reload if preferences have already been loaded (not during initial load)
    if (user && user.id && availabilityPreference && !preferencesLoading) {
      loadUserAvailability();
    }
  }, [availabilityPreference]);

  // Handle date range changes - reload availability to fill in new dates
  useEffect(() => {
    if (user && user.id && !preferencesLoading && customDateRange.endDate) {
      loadUserAvailability();
    }
  }, [customDateRange.endDate]);

  // When the toggle changes (or the user's week-start preference loads),
  // recompute the date range. 'custom' leaves customDateRange alone so the
  // user's last picker selection persists.
  useEffect(() => {
    const range = computeRangeForMode(dateRangeMode, weekStartsOn);
    if (range) setCustomDateRange(range);
  }, [dateRangeMode, weekStartsOn]);

  // After the week-start preference loads, decide the default toggle once.
  // If "This Week" would show fewer than 2 days (e.g. it's Friday or later
  // with a Monday-start week), switch the default to "Next Week".
  useEffect(() => {
    if (preferencesLoading) return;
    if (hasAppliedDefaultModeRef.current) return;
    hasAppliedDefaultModeRef.current = true;
    const thisWeek = computeRangeForMode('thisWeek', weekStartsOn);
    if (rangeDayCount(thisWeek) < 2) {
      setDateRangeMode('nextWeek');
    }
  }, [preferencesLoading, weekStartsOn]);

  // Cancel any pending auto-save timer when the component unmounts.
  useEffect(() => () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
  }, []);

  const loadUserAvailability = async () => {
    // Tag this load with the range it's for, so we can drop its result if a
    // different range is selected (e.g. the mount-time default auto-switch from
    // "This Week" to "Next Week") before this request resolves.
    const loadRangeKey = rangeKeyOf(customDateRange);
    setAvailabilityLoading(true);
    setSaveError(''); // Clear any previous errors

    try {
      // Check if user is fully authenticated
      if (!user || !user.id) {
        console.warn('⚠️  User not authenticated, skipping availability load');
        setAvailabilityData({});
        return;
      }
      const { data, error, isCalendarConnected, syncDateRange } = await getUserAvailability(availabilityPreference, user);

      // Bail out if a newer range was selected while this request was in flight
      // - committing now would overwrite the active range's data.
      if (loadRangeKey !== activeRangeKeyRef.current) return;

      // Update calendar connection status
      setIsCalendarConnected(isCalendarConnected || false);

      if (error) {
        console.error('❌ Error loading availability:', error);
        setSaveError(`Failed to load ${availabilityPreference} times: ${error.message || 'Unknown error'}`);
        setAvailabilityData({});
        setHasExistingAvailability(false);
      } else {
        let availabilityData = data || {};
        console.log('loadUserAvailability - received data:', JSON.stringify(availabilityData));
        console.log('loadUserAvailability - isCalendarConnected:', isCalendarConnected);
        console.log('loadUserAvailability - availabilityPreference:', availabilityPreference);
        console.log('loadUserAvailability - syncDateRange:', syncDateRange);

        // Fill in missing dates with full availability when calendar is connected and mode is "available"
        if (isCalendarConnected && availabilityPreference === 'available') {
          console.log('Filling in missing dates with full availability for calendar-connected user');
          const dateRange = generateDateRange();
          const filledData = { ...availabilityData };

          if (syncDateRange) {
            // If we have sync date range, only fill dates within that range
            const syncStartDate = new Date(syncDateRange.startDate);
            const syncEndDate = new Date(syncDateRange.endDate);

            dateRange.forEach(dateObj => {
              const currentDate = new Date(dateObj.date);
              const isWithinSyncRange = currentDate >= syncStartDate && currentDate <= syncEndDate;

              if (isWithinSyncRange && !filledData[dateObj.date]) {
                // Date is within sync range and has no calendar events = fully available
                filledData[dateObj.date] = [{ start: '00:00', end: '24:00' }];
              }
            });
          } else {
            // No sync date range available - fill all dates in the current view range
            // This handles ICS subscriptions and cases where sync logs don't exist
            console.log('No sync date range - filling all dates in view range');
            dateRange.forEach(dateObj => {
              if (!filledData[dateObj.date]) {
                // No busy events for this date = fully available
                filledData[dateObj.date] = [{ start: '00:00', end: '24:00' }];
              }
            });
          }

          availabilityData = filledData;
          console.log('loadUserAvailability - after filling missing dates:', JSON.stringify(availabilityData));
        }

        setAvailabilityData(availabilityData);
        // Check if user has any existing availability data
        setHasExistingAvailability(Object.keys(availabilityData).length > 0);
      }
    } catch (error) {
      if (loadRangeKey !== activeRangeKeyRef.current) return;
      console.error('❌ Exception in loadUserAvailability:', error);
      setSaveError(`Failed to load ${availabilityPreference} times: ${error.message || 'Unknown error'}`);
      setAvailabilityData({});
      setHasExistingAvailability(false);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // Builds the day-strip descriptors for the active range. Thin wrapper around
  // the shared helper so existing call sites stay unchanged.
  const generateDateRange = () => buildDateRange(customDateRange);

  // Save availability to the server. Silent by default - no loading spinner,
  // no success banner. Errors still surface via setSaveError.
  // dataOverride lets the auto-save timer save the freshest state even if
  // React hasn't committed it to availabilityData yet.
  const handleSaveAvailability = async (dataOverride = null) => {
    const dataToSave = dataOverride ?? availabilityData;
    setSaveError('');

    try {
      const { error } = await saveUserAvailability(dataToSave, availabilityPreference, user);
      if (error) {
        console.error('Error saving availability:', error);
        setSaveError(error.message || 'Failed to save availability. Please try again.');
        return;
      }
      // Track availability save event
      if (user?.id) {
        if (hasExistingAvailability) {
          trackUpdateAvailability(user.id);
        } else {
          trackSetAvailability(user.id);
        }
      }
      setHasExistingAvailability(true);
    } catch (error) {
      console.error('Error saving availability:', error);
      setSaveError('Failed to save availability. Please try again.');
    }
  };

  // Debounced auto-save. Resets the timer on each call so a burst of edits
  // results in a single save shortly after the user stops interacting.
  const scheduleAutoSave = (data) => {
    latestAvailabilityRef.current = data;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleSaveAvailability(latestAvailabilityRef.current);
    }, 800);
  };

  // Clear error message after 5 seconds
  useEffect(() => {
    if (saveError) {
      const timer = setTimeout(() => setSaveError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [saveError]);

  // Handle time range changes for a specific date
  const handleTimeRangesChange = (dateKey, timeRanges) => {
    // Prevent editing if calendar sync is enabled
    if (isCalendarConnected) {
      alert('Calendar sync is enabled. Manual availability editing is disabled.\n\nTo edit your availability, go to Settings and disconnect your calendar, or disable calendar sync.');
      return;
    }

    setAvailabilityData(prev => {
      const next = { ...prev, [dateKey]: timeRanges };
      // Trigger debounced auto-save with the freshest state
      scheduleAutoSave(next);
      return next;
    });
  };

  const clearAllAvailability = async () => {
    const confirmMessage = availabilityPreference === 'available' 
      ? 'Are you sure you want to clear all availability data? This will permanently delete your saved availability.'
      : 'Are you sure you want to clear all busy times data? This will permanently delete your saved busy times.';
      
    if (window.confirm(confirmMessage)) {
      setLoading(true);
      setSaveError('');

      try {
        const { error } = await clearUserAvailability(user);
        if (error) {
          console.error('Error clearing availability:', error);
          setSaveError('Failed to clear availability. Please try again.');
        } else {
          setAvailabilityData({});
        }
      } catch (error) {
        console.error('Error clearing availability:', error);
        setSaveError('Failed to clear availability. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // NEW: Get preference-aware text and styling
  const getPreferenceText = () => {
    return availabilityPreference === 'available' ? {
      title: 'Your Availability',
      subtitle: 'Set when you\'re available for a round and to help find the perfect tee times in your groups',
      timelineTitle: 'Golf Availability Timeline',
      timelineSubtitle: 'Set your golf availability using time slots. Choose when you\'re free to play golf with your groups.',
      clearButtonText: 'Clear All',
      saveButtonText: 'Save Availability Settings',
      helpTitle: 'How to set your availability:',
      whySetTitle: 'Why set availability?'
    } : {
      title: 'Your Busy Times',
      subtitle: 'Set when you\'re busy to help find the perfect tee times in your groups',
      timelineTitle: 'Busy Times Timeline',
      timelineSubtitle: 'Block out your busy times using time slots. Choose when you\'re NOT available to play golf with your groups.',
      clearButtonText: 'Clear All',
      saveButtonText: 'Save Busy Times Settings',
      helpTitle: 'How to set your busy times:',
      whySetTitle: 'Why set busy times?'
    };
  };

  const preferenceText = getPreferenceText();

  // Handle date range mode changes
  const handleDateRangeChange = (startDate, endDate) => {
    setCustomDateRange({ startDate, endDate });
  };


  const dates = generateDateRange();


  if (preferencesLoading || availabilityLoading) {
    return (
      <>
        <Helmet>
          <title>My Availability - TeeSync</title>
        </Helmet>
        <AvailabilitySkeleton />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-app">
      <Helmet>
        <title>My Availability - TeeSync</title>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <PageTransition className="space-y-6">
          {/* Main Control Panel - flattened: sections sit directly on the page
              background instead of nesting inside an outer card. Each child
              section already manages its own bottom margin. */}
          <div>
              {/* Date Range Toggle - same segmented-pill pattern as the Friends
                  page tabs. Drives which days appear below. */}
              <div className="flex space-x-1 bg-surface rounded-lg p-1 mb-4">
                {[
                  { id: 'thisWeek', label: 'This Week' },
                  { id: 'nextWeek', label: 'Next Week' },
                  { id: 'custom', label: 'Custom' },
                ].map((opt) => {
                  const active = dateRangeMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setDateRangeMode(opt.id)}
                      className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 min-h-[44px] ${
                        active
                          ? 'bg-card text-text-primary shadow-xs'
                          : 'text-text-muted hover:text-text-secondary'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* Custom date picker - only visible when "Custom" is selected */}
              {dateRangeMode === 'custom' && (
                <div className="mb-4">
                  <DateRangePicker
                    startDate={customDateRange.startDate}
                    endDate={customDateRange.endDate}
                    onChange={handleDateRangeChange}
                    placeholder="Select date range"
                    className="w-full"
                  />
                </div>
              )}

              {/* Reset and Calendar utility buttons removed - Reset can be
                  achieved per-day via the Clear button on each day card,
                  and Calendar settings live on the Settings page. */}

              {/* Success banner removed - auto-save is silent on success.
                  Errors still surface via the Error Message banner below. */}

              {/* Error Message */}
              {saveError && (
                <motion.div
                  initial={{ opacity: 0, y: -20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  className="mb-4 sm:mb-6 p-4 sm:p-6 bg-status-error/10 border border-status-error/30 text-status-error rounded-xl sm:rounded-lg flex items-center"
                >
                  <div className="w-8 h-8 bg-status-error rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-status-error mb-1">Save Failed</p>
                    <p className="text-sm text-status-error">{saveError}</p>
                  </div>
                </motion.div>
              )}

              {/* Availability Interface - Respects user preference but no toggle shown */}
              {!availabilityLoading && !saveError.includes('Failed to load') && (
                <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                  {dates.map((dateObj) => (
                    <div key={dateObj.date}>
                      {/* Timeline-based availability picker (the only mode). */}
                      <TimelineAvailability
                        dateKey={dateObj.date}
                        dateLabel={dateObj.fullDate}
                        timeRanges={availabilityData[dateObj.date] || []}
                        onTimeRangesChange={(timeRanges) => handleTimeRangesChange(dateObj.date, timeRanges)}
                        availabilityPreference={availabilityPreference}
                        disabled={isCalendarConnected}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Save button + Load More Days buttons removed.
                  Availability now auto-saves via debounced scheduleAutoSave
                  whenever a time range changes. Date range is controlled by
                  the toggle / Custom date picker at the top of the section. */}
          </div>

          {/* Data Loading Error State */}
          {!availabilityLoading && saveError && saveError.includes('Failed to load') && (
            <div>
              <div className="text-center py-8 sm:py-12 bg-card rounded-lg sm:rounded-lg border border-status-error/30">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-status-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiX} className="w-6 h-6 sm:w-8 sm:h-8 text-status-error" />
                </div>
                <h3 className="text-status-error font-bold text-sm sm:text-base mb-2">
                  Failed to Load {availabilityPreference === 'available' ? 'Availability' : 'Busy Times'}
                </h3>
                <p className="text-status-error text-xs sm:text-sm mb-4">
                  {saveError}
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setSaveError('');
                    loadUserAvailability();
                  }}
                  className="px-4 py-2 bg-status-error text-white rounded-lg text-sm font-medium hover:brightness-90 transition-all duration-200"
                >
                  Try Again
                </motion.button>
              </div>
            </div>
          )}

          {/* Bottom prompt card - mirrors the dashboard's accent suggested-action
              card. Nudges the user toward the next step based on their state. */}
          {!availabilityLoading && (() => {
            const hasAnyRanges = Object.values(availabilityData)
              .some(ranges => Array.isArray(ranges) && ranges.length > 0);

            const card = hasAnyRanges
              ? {
                  to: '/create-round',
                  label: 'Suggested',
                  title: "You're ready to play",
                  subtitle: "Schedule a round and we'll find a tee time that works.",
                }
              : {
                  to: null,
                  label: 'Get started',
                  title: 'Set your availability',
                  subtitle: 'Add the times you’re free to play so we can start scheduling tee times.',
                };

            const inner = (
              <div className="bg-accent rounded-lg p-5 cursor-pointer hover:bg-interactive-hover transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-card/20 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiZap} className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="block text-[10px] font-semibold uppercase tracking-wider text-white/70 mb-0.5">
                        {card.label}
                      </span>
                      <h3 className="text-base font-medium text-white">{card.title}</h3>
                      <p className="text-white/70 text-sm">{card.subtitle}</p>
                    </div>
                  </div>
                  {card.to && <SafeIcon icon={FiArrowRight} className="w-5 h-5 text-white" />}
                </div>
              </div>
            );

            return (
              <div className="mt-6">
                {card.to
                  ? <Link to={card.to}>{inner}</Link>
                  : (
                    <button
                      type="button"
                      className="block w-full text-left"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      {inner}
                    </button>
                  )
                }
              </div>
            );
          })()}
        </PageTransition>
      </div>
    </div>
  );
}

export default MyAvailability;